<template>
    <div class="event-log-viewer">
        <div class="datagrid-wrapper">
            <div class="row">
                <div class="col-md-12">
                    <h2>{{ title || 'Events' }}</h2>
                    <div class="sub-grid-buttons">
                        <button type="button" @click="fetch(false)" class="btn btn-light">Refresh</button>
                        <button type="button" @click="clearExport()" data-bs-toggle="modal"
                                :data-bs-target="'#' + exportModalID" class="btn btn-light">Export
                        </button>
                        <a id="show-all" class="btn btn-link" v-if="seeAllLink" :href="seeAllLink">See all</a>
                    </div>
                </div>
            </div>

            <div class="row">
                <div class="col-md-12" data-type="grid">
                    <table class="datagrid table">
                        <thead>
                            <tr>
                                <th><h4>Centre</h4></th>
                                <th><h4>Date</h4></th>
                                <th><h4>Time</h4></th>
                                <th><h4>Event</h4></th>
                                <th><h4>Lock</h4></th>
                                <th v-if="type === 'entries'"><h4>User</h4></th>
                                <th><h4>IQ</h4></th>
                            </tr>
                            <tr>
                                <td colspan="1">
                                    <!-- empty -->
                                </td>
                                <td colspan="2">
                                    <form @submit.prevent="fetch()">
                                        <div class="form-group date-filter">
                                            <input type="text" name="dates" class="form-control" @focus="initDatePicker" :value="initialDatesDatePicker" />
                                        </div>
                                    </form>
                                </td>
                                <td>
                                    <form @submit.prevent="fetch()">
                                        <div class="form-group">
                                            <div class="select-wrapper">
                                                <select v-model="filters.event_type" class="form-control"
                                                        @change="fetch()">
                                                    <option :value="null">All events</option>
                                                    <option v-if="isLoadingTypes" disabled>Loading event types...
                                                    </option>
                                                    <option v-for="type in options.type" :value="type.id">
                                                        {{ type.label }}
                                                    </option>
                                                </select>
                                            </div>
                                        </div>
                                    </form>
                                </td>
                                <td>
                                    <lock-select-component
                                        v-if="!userId && !companyId"
                                        :centre-id="filters.centre_id"
                                        v-on:update:lock="lockSelected"
                                    ></lock-select-component>
                                </td>
                                <td v-if="type === 'entries'">
                                    <user-select-component
                                        v-if="!userId && !companyId"
                                        :centre-id="filters.centre_id"
                                        v-on:select:user="userSelected"
                                        v-on:remove:user="userRemoved"
                                    ></user-select-component>
                                </td>
                                <td>
                                </td>
                            </tr>
                        </thead>

                        <tbody>
                            <tr v-if="isLoading">
                                <td colspan="6">
                                    <div class="loading-events"><span class="spinner anim-spin"></span></div>
                                </td>
                            </tr>
                            <template v-if="!isLoading">
                                <tr v-if="events && events.length <= 0">
                                    <td colspan="6" class="text-center text-secondary">No events found with the given
                                        filters.
                                    </td>
                                </tr>
                                <tr v-for="event in events">
                                    <td>{{ event.centre.name }}</td>

                                    <td>{{ $formatDate(event.date, dateFormat) }}</td>
                                    <td>{{ event.time }}</td>
                                    <td>
                                        {{ event.event.description }}
                                        <small v-if="event.event.access_by_description">&nbsp;
                                            {{ event.event.access_by_description }}</small>
                                    </td>
                                    <td>
                                        <a v-if="event.lock && event.lock.id"
                                           :href="renderLockLink(event.lock)">{{ event.lock.description }}</a>
                                        <span
                                            v-if="event.lock && !event.lock.id && event.lock.description">{{
                                                event.lock.description
                                            }}</span>
                                        <span v-if="event.lock && event.lock.name"> - {{ event.lock.name }}</span>
                                        <span class="text-disabled" v-if="!event.lock || !event.lock.description">Unknown</span>
                                    </td>
                                    <td v-if="type === 'entries'">
                                        <template v-if="event.user">
                                            <span v-if="event.user.deleted_at">{{ event.user.name }} (deleted)</span>
                                            <a v-else-if="event.user.type !== 'aca'"
                                               :href="'/employees/' + event.user.id">{{ event.user.name }}</a>
                                            <span v-else>{{ event.user.name }}</span>
                                        </template>
                                        <span v-else class="text-disabled">Unknown</span>
                                    </td>
                                    <td>
                                        <div v-if="event.iq" class="iq-min-width">
                                            <a :href="`/centre/iq/${event.iq.id}`">{{ event.iq.name }}</a>
                                        </div>
                                    </td>
                                </tr>
                            </template>
                            <tr v-if="isLoadingMore">
                                <td colspan="6" class="text-center">
                                    <div class="loading-events">
                                        <span class="spinner anim-spin"></span> <br/>
                                        Loading more events...
                                    </div>
                                </td>
                            </tr>
                            <tr v-if="!isLoading && !isLoadingMore && hasMore">
                                <td colspan="6" class="text-center">
                                    <button @click="fetch(true)" type="button" class="btn btn-light">Load more
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <!-- Modal -->
        <div class="modal fade" :id="exportModalID" tabindex="-1" role="dialog">
            <div class="modal-dialog" role="document">
                <div class="modal-content">

                    <div class="modal-header opaque-header">
                        <div class="clearfix">
                            <h3 class="modal-title"><strong>Exporting events</strong></h3>
                        </div>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>

                    <div class="modal-body">
                        <div>
                            Select the way you would like to export the events:
                        </div>
                        <div v-if="exportFiles && exportFiles.length > 0" class="container-fluid">
                            <div class="row">
                                <div class="col-md-12">
                                    <p><b>Latest export files (latest 5 in last 24h) to download</b></p>
                                </div>
                            </div>
                            <div class="row" v-for="exportFile in exportFiles">
                                <div class="col-md-8">
                                    {{ exportFile.type }} report from {{ exportFile.timeAgo }}
                                </div>
                                <div class="col-md-4 text-right" v-if="!exportFile.is_ready">
                                    Preparing...
                                </div>
                                <div class="col-md-4 text-right" v-if="exportFile.is_ready">
                                    <a
                                        v-if="exportFile.is_successful"
                                        :href="downloadEndpoint(exportFile.id)"
                                        target="_blank">
                                        Download
                                    </a>

                                    <p v-if="!exportFile.is_successful">Failed</p>
                                </div>
                            </div>

                            <hr>
                        </div>


                        <p>The events will be exported according to the filters you've set previously. Make sure
                            those are correct before proceeding.</p>

                        <div v-if="!isLoadingExport && !exportURL">
                            <div class="form-group">
                                <label>Max events to export</label>
                                <select class="form-control" v-model="exportMax">
                                    <option value="100">100</option>
                                    <option value="500">500</option>
                                    <option value="1000">1000</option>
                                    <option value="2000">2000</option>
                                    <option value="5000">5000</option>
                                    <option value="10000">10000</option>
                                </select>
                                <div class="text-warning" v-if="exportMax >= 2000">
                                    Warning: exporting {{ exportMax }} events will take
                                    ~{{ exportTimeEstimate }}min
                                </div>
                            </div>
                        </div>

                        <div v-if="isLoadingExport" class="text-center">
                            <div class="loading-events"><span class="spinner anim-spin"></span></div>
                            <div>
                                Generating file, please wait...<br/>
                                <small>This can take up to {{ exportTimeEstimate }} min</small>
                            </div>
                        </div>

                        <div v-if="!isLoadingExport && exportURL">
                            <div class="text-success text-center" style="margin: 15px 0">
                                Export file is being prepared! You can wait or revisit the page a few minutes
                                later to
                                download your export.
                            </div>
                        </div>
                        <div v-if="!isLoadingExport && exportError">
                            <div class="text-danger text-center" style="margin: 15px 0">Failed to generate
                                export!
                            </div>
                            <div>Please contact Tech Support with the following error:</div>
                            <div>
                                <code>{{ exportError }}</code>
                            </div>
                        </div>

                    </div>

                    <div class="modal-footer">
                        <button @click="exportEvents()" v-if="!exportURL" class="btn btn-danger"
                                :class="{'disabled': isLoadingExport}" :disabled="isLoadingExport">Export
                        </button>
                        <button class="btn btn-light" v-if="exportURL" data-bs-toggle="modal"
                                :data-bs-target="'#' + exportModalID">Close
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>


<style scoped>

.event-log-viewer {
    padding: 20px 0;
}

.text-disabled {
    color: #aaa;
}

.date-filter {
    width: 250px;
}

.select-wrapper {
    display: block;
    min-width: 200px;
}

.iq-min-width {
    min-width: 100px;
}

.sb-search-input, .sb-icon-search {
    border-radius: 4px;
    height: 36px;
}

.datagrid thead > tr > td {
    padding-left: 0;
}

.datagrid .form-group {
    margin-bottom: 0;
    height: 36px;
}

.search-form-mask {
    height: 36px;
}
</style>

<script>
import Events from '../Services/Events'
import StaticCache from '../Services/StaticCache'
import UserSelectComponent from './UserSelectComponent.vue'
import LockSelectComponent from './LockSelectComponent.vue'
import API from '../Services/API'
import Endpoints from '../Config/Endpoints'
import dayjs from "dayjs";
import {TempusDominus} from "@eonasdan/tempus-dominus";

const MAX_DATE_RANGE_DAYS = 90;

export default {

    props: ['max', 'title', 'type', 'seeAllLink', 'centreId', 'companyId', 'userId', 'lockId', 'inventoryId', 'dateFormat'],

    data: () => {
        return {

            exportModalID: 'events_export_' + Math.floor((Math.random() * 10000)),

            events: [],
            isLoading: false,
            isLoadingMore: false,
            isLoadingTypes: false,
            isLoadingExport: false,

            visible: false,

            hasMore: false,

            exportMax: 100,
            exportURL: null,
            exportError: null,
            exportFiles: [],

            filters: {
                date: null,
                event_type: null,
                lock_name: null,
                user_name: null,
                user_ids: [],
                next: null,
            },

            options: {
                type: [],
            },
        }
    },

    mounted: function () {

        this.$forceUpdate();


        if (this.centreId) this.filters.centre_id = this.centreId;
        if (this.userId) this.filters.user_id = this.userId;
        if (this.companyId) this.filters.company_id = this.companyId;
        if (this.inventoryId) this.filters.inventory_id = this.inventoryId;
        if (this.lockId) this.filters.lock_id = this.lockId;

        this.filters.date = [
            dayjs().subtract(6, 'days').format(this.jsDateFormat),
            dayjs().format(this.jsDateFormat)
        ];

        this.fetchEventTypes();

        this.pollLatestExports();

        this.fetch();
    },

    computed: {
        exportTimeEstimate: function () {
            if (this.exportMax >= 10000) return 5;
            if (this.exportMax >= 5000) return 3;
            if (this.exportMax >= 2000) return 2;
            return 1;
        },

        calculatedType: function () {
            if (!this.type) {
                return "entries";
            }

            return this.type;
        },
        datePickerDateFormat: function() {
            return this.dateFormat.toUpperCase()
                .replace("Y", "yyyy")
                .replace("M", "MM")
                .replace("D", "dd");
        },
        jsDateFormat: function() {
            return this.dateFormat.toUpperCase()
                .replace("Y", "YYYY")
                .replace("M", "MM")
                .replace("D", "DD");
        },
        initialDatesDatePicker: function () {
            if (!this.filters.date || !Array.isArray(this.filters.date)) {
                return '';
            }

            return this.filters.date.join(' - ');
        }
    },

    methods: {

        userSelected: function (user) {
            this.filters.user_ids.push(user.id);
            this.$nextTick(() => {
                this.fetch();
            })
        },
        userRemoved: function (user) {
            this.filters.user_ids = this.filters.user_ids.filter(function (item) {
                return item !== user.id
            })
            this.$nextTick(() => {
                this.fetch();
            })
        },
        lockSelected: function (lock) {
            this.filters.lock_id = lock.id;
            this.$nextTick(() => {
                this.fetch();
            })
        },

        fetchLatestExports: function () {
            if (this.centreId) {
                Events.getExports('centre', this.centreId).then((exportFiles) => {
                    exportFiles.map((exportFile) => exportFile.timeAgo = dayjs(exportFile.created_at).fromNow())
                    this.exportFiles = exportFiles;
                });
            } else if (this.userId) {
                Events.getExports('user', this.userId).then((exportFiles) => {
                    exportFiles.map((exportFile) => exportFile.timeAgo = dayjs(exportFile.created_at).fromNow())
                    this.exportFiles = exportFiles;
                });
            }
        },

        pollLatestExports: function () {
            this.fetchLatestExports();

            setTimeout(this.pollLatestExports, 5000);
        },

        fetchEventTypes: function () {

            if (StaticCache.has('EventTypes')) {
                this.options.type = StaticCache.get('EventTypes');
            }

            this.isLoadingTypes = true;

            Events.getEventTypes(this.calculatedType)
                .then((types) => {
                    this.options.type = types;
                    this.isLoadingTypes = false;
                    StaticCache.put('EventTypes', types);
                })
                .catch((err) => {
                    console.log(err);
                    this.isLoadingTypes = false;
                })
        },

        onDateChange: function () {
            this.$forceUpdate();
            this.$nextTick(() => {
                this.fetch();
            })
        },

        fetch: function (shouldFetchNextPage) {

            if (shouldFetchNextPage) {
                this.isLoadingMore = true;
            } else {
                this.filters.next = null;
                this.isLoading = true;
            }

            Events.getEvents(this.calculatedType, this.filters, this.max)
                .then((data) => {
                    this.isLoading = false;
                    this.isLoadingMore = false;

                    this.events = shouldFetchNextPage
                        ? this.events.concat(data.events)
                        : data.events;

                    this.hasMore = !!data.meta.next;

                    if (this.hasMore) {
                        this.filters.next = data.meta.next;
                    }
                })
                .catch((err) => {
                    this.isLoading = false;
                    this.isLoadingMore = false;
                })
        },

        exportEvents: function () {

            this.isLoadingExport = true;

            Events.export(this.calculatedType, this.filters, this.exportMax)
                .then((url) => {
                    this.isLoadingExport = false;
                    this.exportURL = url;

                    this.fetchLatestExports();
                })
                .catch((err) => {
                    console.table(err.headers);
                    this.isLoadingExport = false;
                    this.exportURL = null;
                    this.exportError = JSON.stringify(err.message);
                    console.error(err);
                });

        },

        clearExport: function () {
            this.isLoadingExport = false;
            this.exportURL = null;
            this.exportError = null;
        },

        renderLockLink: (lock) => {
            if (lock.inventory_id) {
                return '/centre/inventory/' + lock.inventory_id;
            }

            return '/centre/lock/' + lock.id;
        },

        downloadEndpoint: (exportId) => {
            return API.url(Endpoints.Events.Download, {
                'exportId': exportId
            })
        },

        initDatePicker() {
            if (this.datePicker) {
                return;
            }
            const input = event.target;
            this.datePicker = new TempusDominus(input, {
                dateRange: true,
                multipleDatesSeparator: ' - ',
                display: {
                    components: {
                        clock: false
                    }
                },
                localization: {
                    format: this.datePickerDateFormat,
                },
                restrictions: {
                    minDate: dayjs().subtract(MAX_DATE_RANGE_DAYS, 'days').toDate(),
                    maxDate: dayjs().toDate()
                }
            });
            input.addEventListener('change.td', (event) => {
                if (!input.value.includes(' - ')) {
                    return;
                }
                let dates = input.value.split(' - ');
                if (!(dates[0] !== this.filters.date[0] || dates[1] !== this.filters.date[1])) {
                    return;
                }
                this.filters.date = dates;
                this.onDateChange();
            })
        },

    },

    components: {
        UserSelectComponent, LockSelectComponent
    },
}
</script>
